import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useAuth } from './Auth';
import { TUIMode } from '../types/model';

interface IThemeContext {
  theme: TUIMode;
  setTheme: (theme: TUIMode) => void;
}

const LOCAL_STORAGE_KEY = 'uiMode';
const getThemeFromStorage = () =>
  (localStorage.getItem(LOCAL_STORAGE_KEY) as TUIMode | null) ?? 'LIGHT';

const ThemeContext = React.createContext<IThemeContext>({
  theme: getThemeFromStorage(),
  setTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState<TUIMode>(getThemeFromStorage());
  const { user } = useAuth();

  const changeTheme = useCallback(function (theme: TUIMode) {
    localStorage.setItem(LOCAL_STORAGE_KEY, theme);
    setTheme(theme);
  }, []);

  useEffect(() => {
    changeTheme(user?.uiMode ?? getThemeFromStorage() ?? 'LIGHT');
  }, [user, user?.uiMode]);

  useLayoutEffect(() => {
    switch (theme) {
      case 'DARK':
        document.documentElement.classList.add('dark');
        break;
      case 'LIGHT':
        document.documentElement.classList.remove('dark');
        break;
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useThemeContext() {
  return useContext(ThemeContext);
}

export function useChartLegendColor() {
  const { theme } = useThemeContext();
  if (theme !== 'DARK') return {};

  return {
    axis: {
      ticks: {
        line: {
          stroke: 'gray',
        },
        text: {
          fill: '#9A9A9A',
        },
      },
    },
    grid: {
      line: {
        stroke: theme === 'DARK' ? '#404044' : '#EAEAEA',
      },
    },
    background: '#1C1C1E',
  };
}
